import { useEffect, useState, Fragment, useRef } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { DownloadTableExcel } from "react-export-table-to-excel";
import { Button, Spinner, Alert } from "react-bootstrap";

const SalesDayBookDetails = ({ formData }) => {
  const tableRef = useRef(null);
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleString().replace(/[/:]/g, "_");
  const [dayBookDetailData, setDayBookDetailData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchData = async (page = 1, append = false) => {
    const isInitialLoad = page === 1;
    if (isInitialLoad) {
      setLoading(true);
      setError(null);
    } else {
      setLoadingMore(true);
    }

    try {
      const fromDateStr = formData.fromDate.toISOString().split("T")[0];
      const toDateStr = formData.toDate.toISOString().split("T")[0];

      // Add timeout for large date ranges
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout for pagination

      const response = await axios.get(
        `${API_BASE_URL}/api/getSalesDayBookDetailData?fromDate=${fromDateStr}&toDate=${toDateStr}&customerName=${formData.name}&page=${page}&limit=100`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      const { data, total, hasMore: moreData, currentLimit } = response.data;

      if (append) {
        setDayBookDetailData((prev) => [...prev, ...data]);
      } else {
        setDayBookDetailData(data);
      }

      setTotalRecords(total);
      setHasMore(moreData);
      setCurrentPage(page);
    } catch (error) {
      console.log(error);
      if (error.name === "AbortError") {
        setError(
          "Request timed out. Please try with a smaller date range or use pagination."
        );
      } else {
        setError("Failed to load data. Please try again.");
      }
      if (!append) {
        setDayBookDetailData([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    setDayBookDetailData([]);
    fetchData(1, false);
  }, [formData]);

  const loadMoreData = () => {
    if (!loadingMore && hasMore) {
      fetchData(currentPage + 1, true);
    }
  };

  const totalSum = dayBookDetailData.reduce(
    (acc, row) => {
      acc.total_amount += row.total_amount || 0;
      acc.discount_on_total += row.discount_on_total || 0;
      acc.round_off += row.round_off || 0;
      acc.bill_amount += row.bill_amount || 0;
      return acc;
    },
    {
      total_amount: 0,
      discount_on_total: 0,
      round_off: 0,
      bill_amount: 0,
    }
  );

  return (
    <Fragment>
      <DownloadTableExcel
        filename={"SalesDayBookDetailReport_" + formattedDate}
        sheet="SalesDayBookDetail_Report"
        currentTableRef={tableRef.current}
      >
        <Button disabled={loading}> Export excel </Button>
      </DownloadTableExcel>

      {error && (
        <Alert variant="danger" className="mt-3">
          {error}
        </Alert>
      )}

      {totalRecords > 0 && (
        <Alert variant="info" className="mt-3">
          Showing {dayBookDetailData.length} of {totalRecords} records.
          {hasMore && " Scroll down to load more data."}
        </Alert>
      )}

      <div className="table-responsive">
        <table
          ref={tableRef}
          className="table table-bordered table-hover table-striped table-info mt-3"
        >
          <thead>
            <tr>
              <th>Invoice No</th>
              <th>Date</th>
              <th>Customer Name</th>
              <th>Discount Type</th>
              <th>Discount Input</th>
              <th>Other Expenses</th>
              <th>MOP</th>
              <th>Tax Type</th>
              <th>OS</th>
              <th>Total Amount</th>
              <th>Discount On Total</th>
              <th>Round Off</th>
              <th>Bill Amount</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th>Product Code</th>
              <th>Product Name</th>
              <th>Height</th>
              <th>Width</th>
              <th>Qty</th>
              <th>Total Sqft</th>
              <th>Purchase Price</th>
              <th>Sales Price</th>
              <th>MRP</th>
              <th>Gross Amt</th>
              <th>Taxable</th>
              <th>CGST %</th>
              <th>CGST</th>
              <th>SGST %</th>
              <th>SGST</th>
              <th>IGST %</th>
              <th>IGST</th>
              <th>Discount %</th>
              <th>Discount</th>
              <th>Sub Total</th>
            </tr>
          </thead>
          {loading && dayBookDetailData.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={35} className="text-center py-4">
                  <Spinner animation="border" role="status" className="me-2">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  Loading sales detail data...
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {dayBookDetailData.length === 0 && !loading ? (
                <tr>
                  <td colSpan={35} className="text-center py-4">
                    No data found for the selected date range.
                  </td>
                </tr>
              ) : (
                <>
                  {dayBookDetailData.map((row, index) => (
                    <tr key={`${row.invoice_no}-${index}`}>
                      <td>{row.invoice_no}</td>
                      <td>{row.invoice_date}</td>
                      <td>{row.customer_name}</td>
                      <td>{row.discount_type}</td>
                      <td>{row.discount_input}</td>
                      <td>{row.other_expenses}</td>
                      <td>{row.mop}</td>
                      <td>{row.tax_type}</td>
                      <td>{row.os}</td>
                      <td>{row.total_amount}</td>
                      <td>{row.discount_on_total}</td>
                      <td>{row.round_off}</td>
                      <td>{row.bill_amount}</td>
                      <td>
                        {row.createdAt &&
                          new Date(row.createdAt).toLocaleString()}
                      </td>
                      <td>
                        {row.updatedAt &&
                          new Date(row.updatedAt).toLocaleString()}
                      </td>
                      <td>{row.product_code}</td>
                      <td>{row.product_name}</td>
                      <td>{row.height}</td>
                      <td>{row.width}</td>
                      <td>{row.qty}</td>
                      <td>{row.total_sqft}</td>
                      <td>{row.purchase_price}</td>
                      <td>{row.sales_price}</td>
                      <td>{row.mrp}</td>
                      <td>{row.gross_amt}</td>
                      <td>{row.taxable}</td>
                      <td>{row.cgstP}</td>
                      <td>{row.cgst}</td>
                      <td>{row.sgstP}</td>
                      <td>{row.sgst}</td>
                      <td>{row.igstP}</td>
                      <td>{row.igst}</td>
                      <td>{row.discountP}</td>
                      <td>{row.discount}</td>
                      <td>{row.sub_total}</td>
                    </tr>
                  ))}
                  {loadingMore && (
                    <tr>
                      <td colSpan={35} className="text-center py-2">
                        <Spinner
                          animation="border"
                          size="sm"
                          role="status"
                          className="me-2"
                        >
                          <span className="visually-hidden">
                            Loading more...
                          </span>
                        </Spinner>
                        Loading more data...
                      </td>
                    </tr>
                  )}
                </>
              )}
              {dayBookDetailData.length > 0 && (
                <tr>
                  <th colSpan={9}>Total</th>
                  <th>{totalSum.total_amount.toFixed(2)}</th>
                  <th>{totalSum.discount_on_total.toFixed(2)}</th>
                  <th>{totalSum.round_off.toFixed(2)}</th>
                  <th>{totalSum.bill_amount.toFixed(2)}</th>
                  <th colSpan={26}></th>
                </tr>
              )}
            </tbody>
          )}
        </table>
      </div>

      {/* Load More Button outside table for better visibility */}
      {hasMore && !loadingMore && dayBookDetailData.length > 0 && (
        <div className="text-center mt-3 mb-3">
          <Button
            variant="outline-primary"
            size="lg"
            onClick={loadMoreData}
            className="px-4"
          >
            Load More Data
          </Button>
        </div>
      )}
    </Fragment>
  );
};

export default SalesDayBookDetails;
